import { AliasInvalidReason } from '../../../shared/ParkingaboModels';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate } from 'react-router';
import { useCustomerContext } from '../../../components/CustomerProvider.tsx';

export interface StepConfig {
    steps: Array<{
        type: OnboardingStepType;
        completed?: boolean;
    }>;
}

export enum OnboardingStepType {
    USER_DATA = 'USER_DATA',
    VEHICLE = 'VEHICLE',
    PAYMENT = 'PAYMENT',
}

const emptyStepConfigSignupByDomain: StepConfig = {
    steps: [
        {
            type: OnboardingStepType.USER_DATA,
        },
        {
            type: OnboardingStepType.VEHICLE,
        },
        {
            type: OnboardingStepType.PAYMENT,
        },
    ],
};

const emptyStepConfigNoSignupVehicleRequired: StepConfig = {
    steps: [
        {
            type: OnboardingStepType.VEHICLE,
        },
        {
            type: OnboardingStepType.PAYMENT,
        },
    ],
};

const emptyStepConfigNoSignupJustPayment: StepConfig = {
    steps: [
        {
            type: OnboardingStepType.PAYMENT,
        },
    ],
};

const OnboardingContext = createContext<StepConfig>(
    emptyStepConfigSignupByDomain,
);

export function OnboardingProvider() {
    const { user, vehicles } = useCustomerContext();
    const [configBase] = useState<StepConfig>(() => {
        if (user.role.hasAppSignupEmailDomains) {
            return emptyStepConfigSignupByDomain;
        }

        return vehicles.data.length > 0
            ? emptyStepConfigNoSignupJustPayment
            : emptyStepConfigNoSignupVehicleRequired;
    });

    const config = useMemo(() => {
        return {
            steps: configBase.steps.map(step => {
                switch (step.type) {
                    case OnboardingStepType.USER_DATA:
                        return {
                            ...step,
                            completed: !!(user.firstName && user.lastName),
                        };
                    case OnboardingStepType.VEHICLE:
                        return { ...step, completed: vehicles.data.length > 0 };
                    case OnboardingStepType.PAYMENT:
                        return {
                            ...step,
                            completed:
                                user.aliasInvalidReason !==
                                    AliasInvalidReason.ONBOARDING_REQUIRED &&
                                user.aliasInvalidReason !==
                                    AliasInvalidReason.NOT_FOUND,
                        };
                    default:
                        return step;
                }
            }),
        };
    }, [configBase, user, vehicles.data]);

    return (
        <OnboardingContext.Provider value={config}>
            <Outlet />
        </OnboardingContext.Provider>
    );
}

export function useOnboarding() {
    return useContext(OnboardingContext);
}

export function useNavigateOnStepCompletion(stepToCheck: number, url: string) {
    const config = useOnboarding();
    const navigate = useNavigate();
    const stepCompleted = config.steps[stepToCheck]?.completed;
    useEffect(() => {
        if (stepCompleted) {
            navigate(url);
        }
    }, [navigate, stepCompleted, url]);
}
